import { get } from 'lodash';
import { observer } from 'mobx-react';
import { PropTypes } from 'prop-types';
import { useRootStore } from '@nandosaus/state-management';
import React from 'react';
import styled from 'styled-components';
import { layout } from 'styled-system';

import { breakpoints } from '../../../constants';
import { Flex } from '../../grid';
import { Icon } from '../../icons';
import { OrderSettingsSummaryItem } from '../order-settings-summary-item';
import { P } from '../../typography/new';
import { useCateringDateValidation } from '../../../hooks/use-catering-date-validation';

const RESTAURANT_NAME_PLACEHOLDER = 'Find a restaurant';

const OrderSettingsSummaryWrapper = styled.button`
  display: flex;
  background-color: ${({ isSticky }) => `rgba(0, 0, 0, ${isSticky ? 1 : 0.7}) !important`};
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  flex-wrap: wrap;
  width: 100%;
  color: ${({ color }) => color};
  fill: ${({ color }) => color};
  padding: 9px 12px;
  ${layout}

  @media (min-width: ${breakpoints.md}) {
    flex-wrap: nowrap;
    width: 494px;
    padding: ${({ noOrderType }) => (noOrderType ? '23.5px 24px' : '10px 12px')};
    border-radius: 8px;
    margin-top: 0px;
  }
`;

const CompactWrapper = styled(Flex)`
  display: flex;
  width: 100%;
  align-items: center;
  color: black;
  background-color: white;
  border-radius: 8px;
  padding-left: 24px;
  padding-right: 24px;
  ${layout}
`;

const OrderSettingsSummaryCta = ({ onClick, isCompact, isModal, disabled, color, isSticky }) => {
  const { DeliveryStore, OrderingContextStore, CartStore } = useRootStore();
  const { validateDateString } = useCateringDateValidation();

  const { formattedDeliveryAddress, formattedDeliveryTimePreference, preferredDeliveryTime } = DeliveryStore;
  const { orderType, isDineIn, isPickUp, isDelivery, isCatering, isCateringDelivery } = OrderingContextStore;

  const WrapperComponent = isCompact ? CompactWrapper : OrderSettingsSummaryWrapper;

  if (!orderType) {
    return (
      <WrapperComponent
        color={color}
        height={!isCompact && { xs: isSticky ? '73px' : '101px', md: '72px' }}
        py={isCompact ? '23.5px' : '0px'}
        type="button"
        as="button"
        data-testid="order-settings-summary"
        onClick={onClick}
        alignItems="start"
        isSticky={isSticky}
        noOrderType
      >
        <Flex
          height={!isCompact && { xs: isSticky ? '28px' : '55px', md: '100%' }}
          alignItems="center"
          width="100%"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <P variant="standard-semi-bold" color="currentColor">
            Select the order type
          </P>
          <Icon name="chevron" fill={isCompact ? '#E81313' : 'current-color'} ml="16px" />
        </Flex>
      </WrapperComponent>
    );
  }

  const summaryCopy = (() => {
    const { fulfilment, restaurant } = OrderingContextStore;

    if (isDineIn) {
      const { tableNumber } = fulfilment;
      const detailLabel = tableNumber ? `Table ${tableNumber}` : 'Table';

      return {
        detailLabel,
        detailIconName: 'markerWithLine',
        hasError: !tableNumber,
        iconName: 'cutlery',
        locationLabel: get(restaurant, 'name', null),
        locationPlaceholder: RESTAURANT_NAME_PLACEHOLDER,
        type: 'Dine in',
      };
    }

    if (isPickUp) {
      const { isAsap, formattedOrderTime } = fulfilment;

      return {
        detailLabel: formattedOrderTime,
        detailIconName: isAsap ? 'clock' : 'schedule',
        iconName: 'shoppingBagChili',
        locationLabel: get(restaurant, 'name', null),
        locationPlaceholder: RESTAURANT_NAME_PLACEHOLDER,
        type: 'Pick up',
      };
    }

    if (isDelivery) {
      return {
        detailLabel: formattedDeliveryTimePreference,
        detailIconName: preferredDeliveryTime === 'ASAP' ? 'clock' : 'schedule',
        iconName: 'delivery',
        locationLabel: formattedDeliveryAddress,
        locationPlaceholder: 'Set a location',
        type: 'Delivery',
      };
    }

    if (isCatering) {
      const { formattedCateringOrderDate, formattedCateringOrderTime, cateringPickupDate } = fulfilment;

      // Use the shared validation hook to check if the catering date is valid
      const isCateringDateValid = validateDateString(
        cateringPickupDate,
        OrderingContextStore.restaurant,
        CartStore.subtotalPrices?.cents,
        orderType
      );

      return {
        detailLabel: formattedCateringOrderDate,
        secondDetailLabel: formattedCateringOrderTime,
        hasError:
          CartStore.isEditingOrder && !CartStore.hasEditedTime
            ? false
            : !formattedCateringOrderDate || !formattedCateringOrderTime || !isCateringDateValid,
        hasSecondDetail: true,
        detailIconName: 'schedule',
        iconName: 'shoppingBagChili',
        locationLabel: get(OrderingContextStore.restaurant, 'name', null),
        locationPlaceholder: RESTAURANT_NAME_PLACEHOLDER,
        type: 'Pick up',
      };
    }

    // Catering delivery: same scheduled date/time as catering pickup, but shown against
    // the delivery address (the restaurant is the address-derived nearest store).
    if (isCateringDelivery) {
      const { formattedCateringOrderDate, formattedCateringOrderTime, cateringPickupDate } = fulfilment;

      const isCateringDateValid = validateDateString(
        cateringPickupDate,
        OrderingContextStore.restaurant,
        CartStore.subtotalPrices?.cents,
        orderType
      );

      return {
        detailLabel: formattedCateringOrderDate,
        secondDetailLabel: formattedCateringOrderTime,
        hasError:
          CartStore.isEditingOrder && !CartStore.hasEditedTime
            ? false
            : !formattedCateringOrderDate ||
              !formattedCateringOrderTime ||
              !isCateringDateValid ||
              !formattedDeliveryAddress,
        hasSecondDetail: true,
        detailIconName: 'schedule',
        iconName: 'delivery',
        locationLabel: formattedDeliveryAddress,
        locationPlaceholder: 'Set a delivery address',
        type: 'Delivery',
      };
    }

    return undefined;
  })();

  return (
    <WrapperComponent
      style={{
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      color={color}
      display="flex"
      type="button"
      onClick={onClick}
      data-testid="order-settings-summary"
      isSticky={isSticky}
      height={!isCompact && { xs: isSticky ? '73px' : '101px', md: '72px' }}
      py={isCompact ? '13px' : '0px'}
      as="button"
    >
      <Flex
        height={!isCompact && { xs: isSticky ? '28px' : '55px', lg: '100%' }}
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Flex width="100%" alignItems="center" overflow="hidden">
          <Flex width="fit-content" alignItems="center" mr="20px">
            <OrderSettingsSummaryItem
              hasSetValue={!!summaryCopy}
              textVariant="standard-link"
              iconName={summaryCopy.iconName}
              value={summaryCopy.type}
              isModal={isModal}
              uppercase
            />
          </Flex>
          <Flex width="fit-content" flexDirection="column" overflow="hidden">
            {!isSticky && (
              <OrderSettingsSummaryItem
                hasError={!!summaryCopy.hasError}
                textVariant="standard"
                hasSetValue={
                  summaryCopy?.hasSecondDetail
                    ? !!summaryCopy.detailLabel && !!summaryCopy?.secondDetailLabel
                    : !!summaryCopy.detailLabel
                }
                hasSecondDetail={summaryCopy?.hasSecondDetail}
                value={
                  summaryCopy?.hasSecondDetail
                    ? [summaryCopy.detailLabel, summaryCopy.secondDetailLabel]
                    : summaryCopy.detailLabel
                }
                isModal={isModal}
              />
            )}
            <OrderSettingsSummaryItem
              hasSetValue={!!summaryCopy.locationLabel}
              value={summaryCopy.locationLabel || summaryCopy.locationPlaceholder}
              truncateText
              isModal={isModal}
            />
          </Flex>
        </Flex>

        <Icon name="chevron" fill="current-color" ml="16px" />
      </Flex>
    </WrapperComponent>
  );
};

OrderSettingsSummaryCta.propTypes = {
  onClick: PropTypes.func.isRequired,
  isCompact: PropTypes.bool,
  isModal: PropTypes.bool,
  disabled: PropTypes.bool,
  color: PropTypes.string,
  isSticky: PropTypes.bool,
};

OrderSettingsSummaryCta.defaultProps = {
  isCompact: false,
  isModal: false,
  disabled: false,
  color: 'white',
  isSticky: false,
};

const ObservedOrderSettingsSummaryCta = observer(OrderSettingsSummaryCta);
export { ObservedOrderSettingsSummaryCta as OrderSettingsSummaryCta };
